/**
 * The platform admin's unified "Invite user" calls. System admin only, through the shared api
 * client and its Bearer token.
 *
 * Every body is read through a guard rather than cast into its interface. Web and the API deploy on
 * separate workflows off the same push, so a new client can meet an older server: an older API
 * answers these routes with 404, which reads as `unsupported` and hides the feature.
 */

import { ApiError, del, get, post } from './api';
import { toInvitation } from './invitations';
import type {
  AdminInviteErrorKind,
  CreateOrganizationInvitationRequest,
  CreateSetupInvitationRequest,
  InviteSport,
  ResolveBlocked,
  ResolveCandidate,
  ResolveInviteResponse,
  ResolveOutcome,
  ResolvePendingInvitation,
  ResolveSchool,
  SetupInvitation,
} from '../types/adminInvites';
import type { Invitation } from '../types/invitations';

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/** A refused admin invite call. */
export class AdminInviteError extends Error {
  readonly kind: AdminInviteErrorKind;
  /** The body's `code`, when it carried one (`invalid_price`, `email_blocked`, ...). */
  readonly code: string | null;
  /** Set on 409 `email_blocked`: the organization the address already belongs to. */
  readonly organization: { id: string; name: string } | null;
  /** Set on a 502 from an org invite: the row was saved and the email did not go out. */
  readonly invitation: Invitation | null;
  /** Set on a 502 from a setup invitation call, for the same reason. */
  readonly setupInvitation: SetupInvitation | null;

  constructor(
    kind: AdminInviteErrorKind,
    message: string,
    extra: {
      code?: string | null;
      organization?: { id: string; name: string } | null;
      invitation?: Invitation | null;
      setupInvitation?: SetupInvitation | null;
    } = {}
  ) {
    super(message);
    this.name = 'AdminInviteError';
    this.kind = kind;
    this.code = extra.code ?? null;
    this.organization = extra.organization ?? null;
    this.invitation = extra.invitation ?? null;
    this.setupInvitation = extra.setupInvitation ?? null;
  }
}

/* ── Defensive readers ───────────────────────────────────────────────────── */

type Rec = Record<string, unknown>;

const asRecord = (value: unknown): Rec =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Rec) : {};

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

const sport = (value: unknown): InviteSport | null =>
  value === 'baseball' || value === 'softball' ? value : null;

const orgRef = (value: unknown): { id: string; name: string } | null => {
  const row = asRecord(value);
  const id = text(row.id);
  const name = text(row.name);
  return id && name ? { id, name } : null;
};

/** Only an unusable row (no id, no address) is dropped. Anything newer just reads as unknown. */
export const toSetupInvitation = (body: unknown): SetupInvitation | null => {
  const row = asRecord(body);
  const id = text(row.id);
  const email = text(row.email);
  if (!id || !email) return null;

  return {
    id,
    email,
    programName: text(row.programName),
    sport: text(row.sport),
    requiresPayment: bool(row.requiresPayment, false),
    customAnnualPriceCents: num(row.customAnnualPriceCents),
    effectiveAnnualPriceCents: num(row.effectiveAnnualPriceCents),
    schoolName: text(row.schoolName),
    status: text(row.status) ?? 'Pending',
    expiresAt: text(row.expiresAt),
    createdAt: text(row.createdAt),
    createdBy: text(row.createdBy),
    sentCount: num(row.sentCount),
    lastSentAt: text(row.lastSentAt),
    lastSendSucceeded: typeof row.lastSendSucceeded === 'boolean' ? row.lastSendSucceeded : null,
  };
};

const toCandidate = (value: unknown): ResolveCandidate | null => {
  const row = asRecord(value);
  const organizationId = text(row.organizationId);
  const name = text(row.name);
  if (!organizationId || !name) return null;

  return {
    organizationId,
    name,
    sport: text(row.sport),
    matchReason: text(row.matchReason) ?? 'domain',
    isPaused: bool(row.isPaused, false),
    awaitingPayment: bool(row.awaitingPayment, false),
    userCount: num(row.userCount) ?? 0,
  };
};

const toPendingInvitation = (value: unknown): ResolvePendingInvitation | null => {
  const row = asRecord(value);
  const id = text(row.id);
  const organizationId = text(row.organizationId);
  if (!id || !organizationId) return null;

  return {
    id,
    organizationId,
    organizationName: text(row.organizationName) ?? 'Unknown organization',
    role: text(row.role) ?? 'user',
    expiresAt: text(row.expiresAt),
  };
};

const toSchool = (value: unknown): ResolveSchool | null => {
  const row = asRecord(value);
  const id = text(row.id);
  const name = text(row.name);
  if (!id || !name) return null;

  return {
    id,
    name,
    state: text(row.state),
    tier: text(row.tier) ?? '',
    annualPriceCents: num(row.annualPriceCents),
  };
};

const toBlocked = (value: unknown): ResolveBlocked | null => {
  const row = asRecord(value);
  const message = text(row.message);
  if (!message) return null;

  return {
    reason: text(row.reason) ?? 'active_user',
    organization: orgRef(row.organization),
    message,
  };
};

const OUTCOMES: readonly ResolveOutcome[] = ['blocked', 'existing_org', 'new_program'];

const listOf = <T>(value: unknown, read: (item: unknown) => T | null): T[] =>
  Array.isArray(value) ? value.map(read).filter((item): item is T => item !== null) : [];

/**
 * A resolve answer the dialog can render, or null when it cannot.
 *
 * `blocked` without its explanation is not renderable, and neither is `existing_org` with no
 * candidate to offer, so both are refused here rather than showing an empty choice.
 */
export const toResolveResponse = (body: unknown): ResolveInviteResponse | null => {
  const row = asRecord(body);
  const email = text(row.email);
  const outcome = OUTCOMES.find((o) => o === row.outcome);
  if (!email || !outcome) return null;

  const blocked = toBlocked(row.blocked);
  const candidates = listOf(row.candidates, toCandidate);
  if (outcome === 'blocked' && !blocked) return null;
  if (outcome === 'existing_org' && candidates.length === 0) return null;

  const program = asRecord(row.newProgram);
  const names = asRecord(program.suggestedNames);

  return {
    email,
    domain: text(row.domain),
    isFreeMailDomain: bool(row.isFreeMailDomain, false),
    outcome,
    blocked,
    school: toSchool(row.school),
    candidates,
    catalogSportsTaken: listOf(row.catalogSportsTaken, text),
    newProgram: {
      suggestedSport: sport(program.suggestedSport),
      suggestedNames: {
        baseball: text(names.baseball),
        softball: text(names.softball),
      },
      catalogAnnualPriceCents: num(program.catalogAnnualPriceCents),
      // Missing means an API that never said Stripe was on, so payment is not offered.
      stripeAvailable: bool(program.stripeAvailable, false),
    },
    pendingSetupInvitation: toSetupInvitation(row.pendingSetupInvitation),
    pendingInvitations: listOf(row.pendingInvitations, toPendingInvitation),
  };
};

/* ── Failure mapping ─────────────────────────────────────────────────────── */

/**
 * Status code (and 409 code) to failure kind.
 *
 * A 404 means two different things depending on what was asked: on a collection or the resolve
 * probe it means an older API with no such endpoints; on one row or one organization it means that
 * thing is gone. The caller says which.
 */
const adminFailure = (
  error: unknown,
  missing: 'unsupported' | 'not_found'
): AdminInviteError => {
  if (!(error instanceof ApiError)) {
    return new AdminInviteError('failed', FALLBACK_MESSAGE);
  }

  const body = asRecord(error.response);
  const code = text(body.code);
  const message = error.message;

  switch (error.statusCode) {
    case 400:
      return new AdminInviteError('invalid', message, { code });
    case 404:
      return new AdminInviteError(missing, message, { code });
    case 409:
      if (code === 'email_blocked') {
        return new AdminInviteError('blocked', message, {
          code,
          organization: orgRef(body.organization),
        });
      }
      if (code === 'payment_unavailable') {
        return new AdminInviteError('payment_unavailable', message, { code });
      }
      return new AdminInviteError('conflict', message, { code });
    case 429:
      return new AdminInviteError('throttled', message, { code });
    case 502: {
      // A 502 carrying the saved row means the email did not go out, so a resend can be offered
      // directly. Both readers are tried: each refuses the other's shape only by missing fields,
      // and the caller reads the one its route returns. A 502 with no row is an upstream lookup
      // that failed before anything was saved (the Firebase check on setup create): a plain failure.
      const invitation = toInvitation(body.invitation);
      const setupInvitation = toSetupInvitation(body.invitation);
      if (!invitation && !setupInvitation) {
        return new AdminInviteError('failed', message, { code });
      }
      return new AdminInviteError('send_failed', message, { code, invitation, setupInvitation });
    }
    case 503:
      return new AdminInviteError('no_transport', message, { code });
    default:
      return new AdminInviteError('failed', message, { code });
  }
};

const unusable = () => new AdminInviteError('failed', FALLBACK_MESSAGE);

/* ── Calls ───────────────────────────────────────────────────────────────── */

/**
 * What the platform knows about an address. A POST so the email never lands in a request-path log.
 * A 404 means an older API: `unsupported`.
 */
export const resolveInvite = async (email: string): Promise<ResolveInviteResponse> => {
  let body: unknown;

  try {
    body = await post<unknown>('/api/admin/invites/resolve', { email });
  } catch (error) {
    throw adminFailure(error, 'unsupported');
  }

  const resolved = toResolveResponse(body);
  if (!resolved) throw unusable();
  return resolved;
};

/**
 * Invites an address into a named organization. A 404 means that organization is missing or
 * deleted. Re-inviting an address with an open invitation re-arms it (200 rather than 201).
 */
export const inviteToOrganization = async (
  organizationId: string,
  request: CreateOrganizationInvitationRequest
): Promise<Invitation> => {
  let body: unknown;

  try {
    // Field by field, so the payload cannot grow anything the server should decide.
    body = await post<unknown>(
      `/api/admin/organizations/${encodeURIComponent(organizationId)}/invitations`,
      { email: request.email, role: request.role }
    );
  } catch (error) {
    throw adminFailure(error, 'not_found');
  }

  const invitation = toInvitation(body);
  if (!invitation) throw unusable();
  return invitation;
};

/** Open setup invitations (pending and expired), newest first. A 404 is the feature probe. */
export const listSetupInvitations = async (): Promise<SetupInvitation[]> => {
  let body: unknown;

  try {
    body = await get<unknown>('/api/admin/setup-invitations');
  } catch (error) {
    throw adminFailure(error, 'unsupported');
  }

  return listOf(body, toSetupInvitation);
};

/**
 * Sends a setup invitation. The price is only sent with payment required, and only when set:
 * leaving it out means the catalog price.
 */
export const createSetupInvitation = async (
  request: CreateSetupInvitationRequest
): Promise<SetupInvitation> => {
  const payload: CreateSetupInvitationRequest = {
    email: request.email,
    requiresPayment: request.requiresPayment,
  };
  if (request.programName?.trim()) payload.programName = request.programName.trim();
  if (request.sport) payload.sport = request.sport;
  if (request.requiresPayment && request.customAnnualPriceCents !== undefined) {
    payload.customAnnualPriceCents = request.customAnnualPriceCents;
  }

  let body: unknown;

  try {
    body = await post<unknown>('/api/admin/setup-invitations', payload);
  } catch (error) {
    throw adminFailure(error, 'unsupported');
  }

  const row = toSetupInvitation(body);
  if (!row) throw unusable();
  return row;
};

/** Sends it again with a new token and expiry. */
export const resendSetupInvitation = async (id: string): Promise<SetupInvitation> => {
  let body: unknown;

  try {
    body = await post<unknown>(`/api/admin/setup-invitations/${encodeURIComponent(id)}/resend`);
  } catch (error) {
    throw adminFailure(error, 'not_found');
  }

  const row = toSetupInvitation(body);
  if (!row) throw unusable();
  return row;
};

/** Withdraws it. The link stops working immediately. */
export const revokeSetupInvitation = async (id: string): Promise<void> => {
  try {
    await del<unknown>(`/api/admin/setup-invitations/${encodeURIComponent(id)}`);
  } catch (error) {
    throw adminFailure(error, 'not_found');
  }
};
